<script setup>
import { ref, onMounted, onBeforeUnmount, watch, defineProps, defineExpose } from 'vue'
import EditorJS from '@editorjs/editorjs'
import Header from '@editorjs/header'
import List from '@editorjs/list'
import Sortable from 'sortablejs'

const props = defineProps({
  initialData: {
    type: Object,
    default: null
  },
  placeholder: {
    type: String,
    default: 'Type \'/\' for commands...'
  },
  headerPlaceholder: {
    type: String,
    default: 'Title...'
  }
})

const emit = defineEmits(['change'])

const editorHolder = ref(null)
let editor = null
let editorReady = null
let sortableInstance = null
let blockObserver = null

const initEditor = async (data = null) => {
  if (editor) {
    // Wait for editor to be ready before destroying
    if (editorReady) {
      try {
        await editorReady
      } catch (e) {
        // Ignore initialization errors
      }
    }
    if (editor && typeof editor.destroy === 'function') {
      await editor.destroy()
    }
    editor = null
    editorReady = null
  }

  // Deep clone data to strip Vue reactivity proxies - EditorJS can't clone Proxy objects
  const plainData = data ? JSON.parse(JSON.stringify(data)) : { blocks: [] }

  const editorConfig = {
    holder: 'editorjs-holder',
    placeholder: props.placeholder,
    tools: {
      header: {
        class: Header,
        config: {
          placeholder: props.headerPlaceholder,
          levels: [1, 2, 3, 4, 5, 6],
          defaultLevel: 1
        }
      },
      paragraph: {
        config: {
          preserveBlank: true
        }
      },
      list: {
        class: List,
        inlineToolbar: true,
        config: {
          defaultStyle: 'unordered'
        }
      },
    },
    data: plainData,
    onReady: () => {
      console.log('Editor.js is ready')
    },
    onChange: () => {
      emit('change')
    },
    style: {
      color: "white"
    }
  }

  editor = new EditorJS(editorConfig)
  editorReady = editor.isReady
  await editorReady
}

const save = async () => {
  if (!editor) {
    return null
  }
  try {
    const outputData = await editor.save()
    return outputData
  } catch (error) {
    console.error('Editor.js save error:', error)
    return null
  }
}


onMounted(async () => {
  await initEditor(props.initialData)

})

onBeforeUnmount(async () => {
  // Destroy MutationObserver
  if (blockObserver) {
    blockObserver.disconnect()
    blockObserver = null
  }

  // Destroy Sortable
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }

  // Destroy Editor
  if (editor) {
    if (editorReady) {
      try {
        await editorReady
      } catch (e) {
        // Ignore initialization errors
      }
    }
    if (editor && typeof editor.destroy === 'function') {
      await editor.destroy()
    }
    editor = null
    editorReady = null
  }
})

watch(() => props.initialData, async (newData) => {
  if (newData) {
    await initEditor(newData)
  }
}, { deep: true })

defineExpose({
  save,
  focus
})
</script>

<template>
  <div class="editorjs-wrapper">
    <div id="editorjs-holder" ref="editorHolder"></div>
  </div>
</template>

<style scoped>
:deep(.ce-toolbar__plus) {
  color: white;
}

:deep(.ce-toolbar__settings-btn) {
  color: white;
}

:deep(.ce-toolbar__plus:hover) {
  background-color: #eff2f51a;
}

:deep(.ce-toolbar__settings-btn:hover) {
  background-color: #eff2f51a;
}

:deep(.ce-block) {
  overflow-wrap: break-word;
}

:deep(.ce-paragraph) {
  overflow-wrap: break-word;
}
</style>
